import cx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ErrorImage from '@assets/images/error-page-image.png';
import { ReactComponent as LogoIcon } from '@assets/svg/logo-w-name.svg';

import Loader from '@components/common/Loader/Loader';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useResetAllFilters from '@hooks/useResetAllFilters';
import useTranslation from '@hooks/useTranslation';

import { useCustomerListQuery } from '@services/useCustomerListQuery';
import { useCustomerSetMutation } from '@services/useCustomerSetMutation';

import { useModalActions } from '@store/useModalStore';

import style from './style.module.scss';

const Error401 = ({ permissions, locationsLength, isOneFacilityActive, customerId, isStructureData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [errorInfo, setErrorInfo] = useState({
    accessTitle: '',
    accessMessage: '',
    multiAccess: false,
  });

  const { resetAllFilter } = useResetAllFilters();

  const { setActiveModal } = useModalActions();

  const { data: customersData } = useCustomerListQuery();

  const { mutate: customerSetMutate } = useCustomerSetMutation();

  useEffect(() => {
    if (
      !permissions &&
      customersData?.customers?.length === 1 &&
      customersData?.customers[0].customer_id !== +customerId
    ) {
      navigate(`/customer/${customersData?.customers[0].customer_id}`, { state: 'SwitchCustomerModal' });

      customerSetMutate({ customer_id: customersData?.customers[0].customer_id });
      resetAllFilter();
    }

    let accessTitle = '';
    let accessMessage = "You haven't access to this page !";
    let multiAccess = false;

    if (!isOneFacilityActive) {
      accessTitle = 'Error: Access to Potential (Inactive) Facility Page Denied.';
      accessMessage =
        'You are trying to login to a Facility that is in the status of Potential. Access to this Facility will appear when it becomes Active';
      multiAccess = customersData?.customers?.length > 1;
    }

    if ((!locationsLength || isStructureData) && permissions) {
      accessTitle = 'Error: Access Denied. You do not have access to the requested facility.';
      accessMessage =
        'Sorry, you do not have access to this facility. Please contact your Admin to grant you access.';
      multiAccess = customersData?.customers?.length > 1;
    }

    if (!permissions) {
      accessTitle = 'Error: Access Denied. You do not have permission to access this site.';
      accessMessage =
        'Needs permission to access resources in your organization that only a manager can grant. Please ask a manager to grant to this app before you can use it.';
      multiAccess = customersData?.customers?.length > 1 && customersData?.customers?.[0].id !== customerId;
    }

    setErrorInfo({
      accessTitle,
      accessMessage,
      multiAccess,
    });
  }, [permissions, locationsLength, isStructureData, customersData, isOneFacilityActive]);

  const logout = () => window.location.replace(APP_SSO_URL + 'logout-simple/2');

  if (!errorInfo.accessTitle) {
    return <Loader />;
  }

  return (
    <>
      <div className={style.sidebarPanel}>
        <LogoIcon />
      </div>
      <div className={style.errorPage}>
        <div className={style.errorPageTitle}>{t(errorInfo.accessTitle)}</div>
        <div className={style.errorImageWrapper}>
          <img className={style.errorImage} src={ErrorImage} alt="error-image" />
          <div className={style.errorImageWrapperText}>{t('an error has occurred')}</div>
        </div>
        <div className={style.errorPageDescription}>{t(errorInfo.accessMessage)}</div>
        <div className={style.errorPageActions}>
          <button
            type="button"
            className={cx('btn', 'btn-outlined', style.errorPageActionsBtn)}
            onClick={logout}
          >
            {t('Logout')}
          </button>
          {errorInfo.multiAccess && (
            <button
              type="button"
              className={cx('btn', 'btn-outlined', style.errorPageActionsBtn, style.switchCustomerBtn)}
              onClick={() =>
                setActiveModal({
                  name: MODAL_NAMES.switchCustomerModal,
                })
              }
            >
              {t('Switch Customer')}
            </button>
          )}
        </div>
        <a href="mailto:support@waites.net" className={style.supportLink}>
          {t('Contact Support')}
        </a>
      </div>
    </>
  );
};

export default Error401;
